/**
 * 克隆图层输出草稿的瞬时 Canvas 管理
 *
 * 仅服务保存与下载结果生成；私有字段会在配方格式化时被 deepDeletePrivateProps 清理。
 */
import type { IImageLayerProps, ILayerProps } from '@/version-types';

/** 克隆图层携带的瞬时输出数据。 */
export interface ILayerOutputDraft {
    /** 是否已经投影稳定草稿。 */
    projected: boolean;
    /** 保存封面只消费已有特效截图，不触发新的 WASM 渲染。 */
    usePersistedImageEffect?: boolean;
    /** 尚未应用图层变换的图片本体。 */
    sourceCanvas?: HTMLCanvasElement;
    /** 原始预览底图身份，仅用于匹配已经完成的特效，不参与配方序列化。 */
    sourceCanvasIdentity?: HTMLCanvasElement;
    /** 与投影参数匹配的已完成特效，保存只序列化此快照。 */
    effectCanvas?: HTMLCanvasElement;
    /** 与 effectCanvas 同次渲染得到的逻辑边界。 */
    effectPosition?: NonNullable<IImageLayerProps['effect']>['effectPosition'];
}

/** 带瞬时输出草稿的克隆图层。 */
export type ILayerWithOutputDraft = ILayerProps & {
    __outputDraft?: ILayerOutputDraft;
};

/**
 * 复制 Canvas，隔离插件后续复用或销毁
 *
 * @param source - 当前稳定预览 Canvas
 * @returns 与输入尺寸一致的独占 Canvas
 */
export const cloneOutputCanvas = (source: HTMLCanvasElement) => {
    const canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;
    canvas.getContext('2d')?.drawImage(source, 0, 0);
    return canvas;
};

/**
 * 获取克隆图层的瞬时输出草稿
 *
 * @param layer - 待查询图层
 * @returns 已投影数据；不存在时返回 undefined
 */
export const getLayerOutputDraft = (layer: ILayerProps) => (layer as ILayerWithOutputDraft).__outputDraft;

/**
 * 标记图层已经投影稳定草稿
 *
 * @param layer - 仅用于输出的克隆图层
 * @returns 可继续写入的瞬时输出数据
 * 副作用：只更新传入的克隆图层。
 */
export const markLayerOutputDraft = (layer: ILayerProps) => {
    const outputLayer = layer as ILayerWithOutputDraft;
    outputLayer.__outputDraft ??= { projected: true };
    outputLayer.__outputDraft.projected = true;
    return outputLayer.__outputDraft;
};

/**
 * 捕获尚未应用图层变换的稳定源 Canvas
 *
 * @param layer - 仅用于输出的克隆图层
 * @param source - 当前稳定图片本体
 * 副作用：复制 Canvas 并写入传入的克隆图层。
 */
export const setLayerOutputSourceCanvas = (layer: ILayerProps, source: HTMLCanvasElement) => {
    const draft = markLayerOutputDraft(layer);
    draft.sourceCanvas = cloneOutputCanvas(source);
    draft.sourceCanvasIdentity = source;
};
